import React, {
  FunctionComponent,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./ThreadSelector.module.css";
import ThreadCard from "./ThreadCard";
import {
  ModelDataType,
  ReceivedModelType,
  SelectedThreadsInfoType,
} from "../../types/RenderTypes";
import { ThreadCardInfo } from "../../types/ConfigTypes";
import { ThreadsType } from "../../types/ThreadsType";
import ComponentRender from "../ThreeJS/ModelRender";

type PropsType = {
  specialModelRender?: boolean;
  model?: ReceivedModelType;
  info?: ModelDataType;
  onChange: (selections: SelectedThreadsInfoType[]) => void;
  selections?: SelectedThreadsInfoType[];
  threadList: ThreadsType[];
};

function groupFeatures(
  items: { diameter: number; depth: number; faceIds: number[] }[],
  type: string,
  groups: ThreadCardInfo[],
  threadList: ThreadsType[]
) {
  items.forEach((item) => {
    const group = groups.find(
      (g) =>
        g.diameter === item.diameter &&
        g.depth === item.depth &&
        g.type === type
    );
    if (group) {
      group.holes.push({ id: item.faceIds[0], faceIds: item.faceIds });
    } else {
      const compThreads = threadList.filter(
        (thread) =>
          thread.maxDiameter >= item.diameter &&
          thread.minDiameter <= item.diameter
      );
      groups.push({
        diameter: item.diameter,
        depth: item.depth,
        holes: [{ id: item.faceIds[0], faceIds: item.faceIds }],
        type,
        threads: compThreads,
      });
    }
  });
}

const ThreadSelector: FunctionComponent<PropsType> = ({
  specialModelRender,
  model,
  info,
  onChange,
  selections,
  threadList,
}) => {
  const [isAtBottom, setIsAtBottom] = useState<boolean>(false);
  const [isAtTop, setIsAtTop] = useState<boolean>(true);
  const [hovered, setHovered] = useState<number[]>([]);
  const [selection, setSelection] = useState<number[]>();

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [cardRefs, setCardRefs] = useState<
    { ids: number[]; ref: RefObject<any> }[]
  >([]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const target = e.target as HTMLDivElement;
    setIsAtBottom(
      target.scrollHeight - target.scrollTop <= target.clientHeight + 10
    );
    setIsAtTop(target.scrollTop < 10);
  }, []);

  useEffect(() => {
    if (selection && selection.length > 0) {
      cardRefs.forEach((ref) => {
        if (ref.ids.includes(selection[0])) {
          ref.ref.current &&
            ref.ref.current.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
            });
        }
      });
    }
  }, [selection, cardRefs]);

  const groupHoles = useMemo(() => {
    if (!info || !info.features) return [];
    const groups: ThreadCardInfo[] = [];
    info.features.holes.forEach((hole) => {
      groupFeatures(hole.bores, "bore", groups, threadList);
      groupFeatures(hole.counterbores, "counterbore", groups, threadList);
      // Skip countersinks - no thread selection for them
    });
    groupFeatures(info.features.shafts, "shaft", groups, threadList);
    return groups.sort((a, b) => {
      if (a.diameter === b.diameter) {
        return a.depth - b.depth;
      } else {
        return a.diameter - b.diameter;
      }
    });
  }, [info?.features, threadList]);

  const threadCards = useMemo(() => {
    const newCardRefs: { ids: number[]; ref: RefObject<any> }[] = [];
    const res = groupHoles.map((group: ThreadCardInfo, key: number) => {
      if (group.type === "countersink") return null;
      const cardRef = React.createRef<HTMLDivElement>();

      const isHovered =
        group.holes.find((hole) =>
          hovered.find((hover) => hole.faceIds.includes(hover))
        ) !== undefined;
      const isSelected =
        group.holes.find((hole) =>
          selection?.find((select) => hole.faceIds.includes(select))
        ) !== undefined;

      const holeIds = group.holes.map((h) => h.id);
      const cardSelections = (selections ?? []).filter((s) =>
        holeIds.includes(s.id)
      );

      const card = (
        <ThreadCard
          key={key}
          data={group}
          ref={cardRef}
          hover={isHovered || isSelected}
          setHover={setHovered}
          selections={cardSelections}
          onSelectionsChange={(updated: SelectedThreadsInfoType[]) => {
            const otherSelections = (selections ?? []).filter(
              (s) => !holeIds.includes(s.id)
            );
            const withThreads = updated.filter((s) => s.name !== "none");
            onChange([...otherSelections, ...withThreads]);
          }}
        />
      );
      newCardRefs.push({
        ids: group.holes.map((hol) => hol.faceIds).flat(),
        ref: cardRef,
      });
      return card;
    });
    setCardRefs(newCardRefs);
    return res;
  }, [selections, groupHoles, hovered, selection, onChange]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const scrollable = el.scrollHeight - el.scrollTop === el.clientHeight;
    setIsAtBottom(scrollable);
  }, [scrollContainerRef.current]);

  return (
    <div className={styles.container}>
      {model && (
        <div className={styles.canvasContainer}>
          <ComponentRender
            gltf={model}
            info={info}
            specialModelRender={specialModelRender}
            selection={selection}
            setSelection={setSelection}
            hovered={hovered}
            setHovered={setHovered}
          />
        </div>
      )}
      <div className={styles.threadContainer}>
        <div
          className={`${styles.scrollContainer}`}
          ref={scrollContainerRef}
          onScroll={handleScroll}
        >
          {threadCards}
        </div>
        <div
          className={`${styles.shadowContainer} ${
            !isAtTop && styles.shadowTop
          }`}
        />
        <div
          className={`${styles.shadowContainer} ${
            !isAtBottom && styles.shadowBottom
          }`}
        />
      </div>
    </div>
  );
};

export default ThreadSelector;
